/**
 * P1 route code-split: lazy page entry points for AppRoutes (ADR-002).
 * Prefer `@/` imports. Named exports use `.then` to adapt to React.lazy's default.
 *
 * Grouped by product area for easier navigation.
 */
import { lazy } from "react";

// ── Dashboards ──────────────────────────────────────────────────────────────
export const Dashboard = lazy(() =>
  import("@/pages/Dashboard/Dashboard").then((m) => ({
    default: m.Dashboard,
  })),
);
export const TenantDashboard = lazy(
  () => import("@/pages/Dashboard/TenantDashboard"),
);
export const StudyDashboard = lazy(
  () => import("@/pages/Dashboard/StudyDashboard"),
);
export const SiteDashboard = lazy(
  () => import("@/pages/Dashboard/SiteDashboard"),
);
export const SponsorDashboard = lazy(() =>
  import("@/pages/Dashboard/SponsorDashboard").then((m) => ({
    default: m.SponsorDashboard,
  })),
);
export const PrincipalInvestigatorDashboard = lazy(() =>
  import("@/pages/PI").then((m) => ({
    default: m.PrincipalInvestigatorDashboard,
  })),
);
export const StudyCoordinatorDashboard = lazy(() =>
  import("@/pages/UserRoles").then((m) => ({
    default: m.StudyCoordinatorDashboard,
  })),
);
export const SiteAdminDashboard = lazy(() =>
  import("@/pages/SiteManagement").then((m) => ({
    default: m.SiteAdminDashboard,
  })),
);
export const DataManagerDashboard = lazy(() =>
  import("@/pages/DataManager/DataManagerDashboard").then((m) => ({
    default: m.DataManagerDashboard,
  })),
);

// ── Core CTMS ───────────────────────────────────────────────────────────────
export const StudyManagement = lazy(() =>
  import("@/pages/StudyManagement").then((m) => ({
    default: m.StudyManagement,
  })),
);
export const SiteManagement = lazy(() =>
  import("@/pages/SiteManagement").then((m) => ({
    default: m.SiteManagement,
  })),
);
export const UserManagement = lazy(() =>
  import("@/components/UserManagement").then((m) => ({
    default: m.UserManagement,
  })),
);
export const StudyMilestones = lazy(() =>
  import("@/pages/StudyMilestones").then((m) => ({
    default: m.StudyMilestones,
  })),
);
export const MilestoneDetails = lazy(() =>
  import("@/pages/StudyMilestones/components/MilestoneDetails").then((m) => ({
    default: m.MilestoneDetails,
  })),
);
export const SiteActivation = lazy(
  () => import("@/pages/SiteActivation/SiteActivation"),
);
export const SiteFeasibilityPage = lazy(
  () => import("@/pages/SiteFeasibility/SiteFeasibilityPage"),
);
export const MyTasksCTMS = lazy(() =>
  import("@/pages/MyTasks").then((m) => ({ default: m.MyTasksCTMS })),
);
export const AuditLogs = lazy(() => import("@/pages/AuditLogs/AuditLogs"));
export const AuditLogsRoute = lazy(() =>
  import("@/pages/AuditLogs/AuditLogsRoute").then((m) => ({
    default: m.AuditLogsRoute,
  })),
);
export const ProfilePage = lazy(() =>
  import("@/pages/Profile").then((m) => ({ default: m.ProfilePage })),
);
export const SettingsPage = lazy(() =>
  import("@/pages/Settings").then((m) => ({ default: m.SettingsPage })),
);
export const SecurityPolicyPage = lazy(() =>
  import("@/pages/Settings").then((m) => ({ default: m.SecurityPolicyPage })),
);

// ── Monitoring ──────────────────────────────────────────────────────────────
export const MonitoringTracker = lazy(() =>
  import("@/pages/MonitoringTracker").then((m) => ({
    default: m.MonitoringTracker,
  })),
);
export const VisitDetailsPage = lazy(() =>
  import("@/pages/MonitoringTracker/components/VisitDetailsPage").then((m) => ({
    default: m.VisitDetailsPage,
  })),
);
export const MonitoringOversightDashboard = lazy(() =>
  import("@/pages/MonitoringOversight").then((m) => ({
    default: m.MonitoringOversightDashboard,
  })),
);

// ── Regulatory / CDA / Quality ──────────────────────────────────────────────
export const RegulatoryApprovalsMain = lazy(
  () => import("@/pages/Regulatory/RegulatoryApprovalsMain"),
);
export const ApprovalDetailPage = lazy(
  () => import("@/pages/Regulatory/ApprovalDetailPage"),
);
export const CDAManagement = lazy(() =>
  import("@/pages/CDA/CDAManagement").then((m) => ({
    default: m.CDAManagement,
  })),
);
export const QMSPage = lazy(() =>
  import("@/pages/QMS").then((m) => ({ default: m.QMSPage })),
);
export const IssueRiskTrackerPage = lazy(() =>
  import("@/pages/IssueRiskTracker").then((m) => ({
    default: m.IssueRiskTrackerPage,
  })),
);
export const RBQMCenter = lazy(() =>
  import("@/pages/RBQMCenter").then((m) => ({ default: m.RBQMCenter })),
);
export const ConnectorServicesPage = lazy(
  () => import("@/pages/ConnectorServices/ConnectorServicesPage"),
);
export const FinancePayments = lazy(
  () => import("@/pages/Finance/FinancePayments"),
);

// ── Data Manager / EDC / CDMS (full) ────────────────────────────────────────
export const ActivityHistory = lazy(() =>
  import("@/pages/DataManager/ActivityHistory").then((m) => ({
    default: m.ActivityHistory,
  })),
);
export const DatasetExport = lazy(() =>
  import("@/pages/DataManager/DatasetExport").then((m) => ({
    default: m.DatasetExport,
  })),
);
export const CTMSDataReviewDashboard = lazy(() =>
  import("@/pages/DataManager/ctms/CTMSDataReviewDashboard").then((m) => ({
    default: m.CTMSDataReviewDashboard,
  })),
);
export const CRFDesigner = lazy(() =>
  import("@/pages/DataManager/edc/CRFDesigner").then((m) => ({
    default: m.CRFDesigner,
  })),
);
export const CRFTemplates = lazy(() =>
  import("@/pages/DataManager/edc/CRFTemplates").then((m) => ({
    default: m.CRFTemplates,
  })),
);
export const SubjectsManagement = lazy(() =>
  import("@/pages/DataManager/edc/SubjectsManagement").then((m) => ({
    default: m.SubjectsManagement,
  })),
);
export const VisitDataEntry = lazy(() =>
  import("@/pages/DataManager/edc/VisitDataEntry").then((m) => ({
    default: m.VisitDataEntry,
  })),
);
export const EDCDataReviewPage = lazy(() =>
  import("@/pages/DataManager/edc/DataReviewPage").then((m) => ({
    default: m.DataReviewPage,
  })),
);
export const DataCompletenessDashboard = lazy(() =>
  import("@/pages/DataManager/edc/DataCompletenessDashboard").then((m) => ({
    default: m.DataCompletenessDashboard,
  })),
);
export const ExportsEDC = lazy(() =>
  import("@/pages/DataManager/edc/ExportsEDC").then((m) => ({
    default: m.ExportsEDC,
  })),
);
export const CDMSDataReviewPage = lazy(() =>
  import("@/pages/DataManager/cdms/DataReviewPage").then((m) => ({
    default: m.DataReviewPage,
  })),
);
export const QueryManagementPage = lazy(() =>
  import("@/pages/DataManager/cdms/QueryManagementPage").then((m) => ({
    default: m.QueryManagementPage,
  })),
);
export const SDVSDRPage = lazy(() =>
  import("@/pages/DataManager/cdms/SDVSDRPage").then((m) => ({
    default: m.SDVSDRPage,
  })),
);
export const CodingPage = lazy(() =>
  import("@/pages/DataManager/cdms/CodingPage").then((m) => ({
    default: m.CodingPage,
  })),
);
export const DataLocksPage = lazy(() =>
  import("@/pages/DataManager/cdms/DataLocksPage").then((m) => ({
    default: m.DataLocksPage,
  })),
);
export const DataExtractsPage = lazy(() =>
  import("@/pages/DataManager/cdms/DataExtractsPage").then((m) => ({
    default: m.DataExtractsPage,
  })),
);
export const CDMSDashboardsPage = lazy(() =>
  import("@/pages/DataManager/cdms/CDMSDashboardsPage").then((m) => ({
    default: m.CDMSDashboardsPage,
  })),
);
export const CTMSReportsLanding = lazy(() =>
  import("@/pages/DataManager/reports/CTMSReportsLanding").then((m) => ({
    default: m.CTMSReportsLanding,
  })),
);
export const CTMSReportsHub = lazy(() =>
  import("@/pages/CtmsReports/CTMSReportsHub").then((m) => ({
    default: m.CTMSReportsHub,
  })),
);

// ── eTMF ────────────────────────────────────────────────────────────────────
export const DocumentLibrary = lazy(() =>
  import("@/pages/ETMF/DocumentLibrary").then((m) => ({
    default: m.DocumentLibrary,
  })),
);
export const UploadDocument = lazy(() =>
  import("@/pages/ETMF/UploadDocument").then((m) => ({
    default: m.UploadDocument,
  })),
);
export const QCReview = lazy(() =>
  import("@/pages/ETMF/QCReview").then((m) => ({ default: m.QCReview })),
);
export const ETMFCompletenessDashboard = lazy(() =>
  import("@/pages/ETMF/CompletenessDashboard").then((m) => ({
    default: m.CompletenessDashboard,
  })),
);
export const RetentionArchival = lazy(() =>
  import("@/pages/ETMF/RetentionArchival").then((m) => ({
    default: m.RetentionArchival,
  })),
);
export const InspectionMode = lazy(() =>
  import("@/pages/ETMF/InspectionMode").then((m) => ({
    default: m.InspectionMode,
  })),
);
export const ModelGovernance = lazy(() =>
  import("@/pages/ETMF/ModelGovernance").then((m) => ({
    default: m.ModelGovernance,
  })),
);
export const MigrationReview = lazy(() =>
  import("@/pages/ETMF/MigrationReview").then((m) => ({
    default: m.MigrationReview,
  })),
);

// ── Lite stubs (placeholder pages) ──────────────────────────────────────────
export const CalendarPage = lazy(() => import("@/pages/ETCLite/CalendarPage"));
export const EcrfViewerPage = lazy(
  () => import("@/pages/EDCLite/EcrfViewerPage"),
);
export const FormStatesPage = lazy(
  () => import("@/pages/EDCLite/FormStatesPage"),
);
export const DataReviewPage = lazy(() => import("@/pages/CDMS/DataReviewPage"));
export const QueriesPage = lazy(() => import("@/pages/CDMS/QueriesPage"));
export const CodeListsPage = lazy(() => import("@/pages/CDMS/CodeListsPage"));
export const ExportsPage = lazy(() => import("@/pages/CDMS/ExportsPage"));

// ── Profile / Settings (already listed above with CTMS; kept for clarity) ───
// ProfilePage, SettingsPage, SecurityPolicyPage — see Core CTMS section
